import type { Ref } from 'vue'

// Shared splice + decrement helpers for the admin review-queue stores
// (`useLintNotesStore`, `useSynthesisSuggestionsStore`). The two stores
// front different endpoints and different record types but share the
// same channel-payload shape (`Broadcastable`'s `{event, record,
// open_count}`) and the same two-path UX:
//
//   - On a channel `:created` / `:updated`, splice the row in/replace/
//     out depending on `record.status`; mirror the authoritative
//     `open_count` sidecar into the local ref.
//   - On the user clicking accept/dismiss, splice optimistically on
//     the 202 ack and decrement `openCount` so the list and header
//     badge feel live (the wiki_mutations queue is concurrency 1 and
//     the broadcast can lag).
//
// Both helpers operate on the caller's reactive refs, which keeps the
// store's Pinia identity intact (extracting a full `defineStore`
// factory would split the two stores' setup blocks across two files
// for very little additional payoff). The record type is constrained
// to anything with `{ id: number; status: string }` so the same code
// covers both LintNote and SynthesisSuggestion without unsafe casts.

export interface ReviewQueueRecord {
  id: number
  status: string
}

export interface ReviewQueueChannelPayload<T extends ReviewQueueRecord> {
  event: 'created' | 'updated'
  record: T
  open_count: number
}

// Applies a channel broadcast to the local list. Resolution (anything
// non-pending) splices the row out; a still-pending record replaces
// the existing row or is prepended if not yet in the list. Pre-
// Broadcastable, models emitted `:applied` / `:dismissed` events and
// the splice rule branched on the event name; after the migration the
// event is always `:created` / `:updated` and the resolution decision
// reads off `record.status` instead.
export function applyReviewQueueEvent<T extends ReviewQueueRecord>(
  payload: ReviewQueueChannelPayload<T>,
  list: Ref<T[]>,
  openCount: Ref<number>,
): void {
  openCount.value = payload.open_count
  const idx = list.value.findIndex((x) => x.id === payload.record.id)

  if (payload.record.status !== 'pending') {
    if (idx !== -1) list.value.splice(idx, 1)
    return
  }
  if (idx === -1) {
    list.value.unshift(payload.record)
  } else {
    list.value[idx] = payload.record
  }
}

// Optimistic splice for accept/dismiss. The action endpoint returns
// 202 and the row stays pending until the wiki_mutations queue picks
// the job up; this helper takes the row out of the local list and
// decrements `openCount` on the ack so the SPA feels live without
// waiting for the broadcast. The subsequent `:updated` event is
// idempotent — the row is already gone and its `open_count` sidecar
// overrides our optimistic value with the server's authoritative
// count.
export function spliceReviewQueueRow<T extends ReviewQueueRecord>(
  list: Ref<T[]>,
  openCount: Ref<number>,
  id: number,
): void {
  const idx = list.value.findIndex((x) => x.id === id)
  if (idx === -1) return
  list.value.splice(idx, 1)
  if (openCount.value > 0) openCount.value -= 1
}
